import React from 'react';
import { Input } from '../../ui';

interface ExtractDataConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

const selectClass =
  'w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md';
const labelClass = 'block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1';

export const ExtractDataConfig: React.FC<ExtractDataConfigProps> = ({ config, onChange }) => {
  const parseType: 'cpf' | 'cnpj' | 'date' =
    config.parseType === 'cnpj' ? 'cnpj' : config.parseType === 'date' ? 'date' : 'cpf';
  const outputVariable = String(config.outputVariable || 'extracted_value').trim() || 'extracted_value';
  const isDoc = parseType === 'cpf' || parseType === 'cnpj';

  return (
    <div className="space-y-4">
      {/* Como funciona */}
      <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Como funciona?</h4>
        <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc list-inside space-y-1">
          <li>Analisa o texto de origem e tenta identificar <strong>{parseType === 'date' ? 'uma data' : `um ${parseType.toUpperCase()}`}</strong>.</li>
          <li>Se identificar, segue pela saída <span className="font-medium text-green-600 dark:text-green-400">Encontrado</span>.</li>
          <li>Se não, segue por <span className="font-medium text-red-600 dark:text-red-400">Não encontrado</span>.</li>
          <li>Preenche: <code>{`{{${outputVariable}}}`}</code>, <code>{`{{${outputVariable}}}_raw`}</code>, <code>{`{{${outputVariable}}}_found`}</code>.</li>
        </ul>
      </div>

      {/* O que identificar */}
      <div>
        <label className={labelClass}>O que identificar</label>
        <select
          className={selectClass}
          value={parseType}
          onChange={(e) => onChange('parseType', e.target.value)}
        >
          <option value="cpf">CPF</option>
          <option value="cnpj">CNPJ</option>
          <option value="date">Data</option>
        </select>
      </div>

      {/* Texto de origem */}
      <Input
        label="Texto de origem"
        placeholder="{{message}}"
        value={config.source ?? ''}
        onChange={(e) => onChange('source', e.target.value)}
        helperText="Aceita variáveis. Vazio ou {{message}} usa a última mensagem do cliente."
      />

      {/* Variável de saída */}
      <Input
        label="Variável de saída"
        placeholder="extracted_value"
        value={config.outputVariable ?? ''}
        onChange={(e) => onChange('outputVariable', e.target.value.replace(/\s/g, '_'))}
        helperText="Nome da variável que recebe o valor normalizado."
      />

      {/* Formato do documento (CPF/CNPJ) */}
      {isDoc && (
        <div>
          <label className={labelClass}>Formato do {parseType.toUpperCase()}</label>
          <select
            className={selectClass}
            value={config.docOutputFormat || 'digits'}
            onChange={(e) => onChange('docOutputFormat', e.target.value)}
          >
            <option value="digits">Só números (ex.: {parseType === 'cpf' ? '12345678909' : '12345678000199'})</option>
            <option value="formatted">Formatado (ex.: {parseType === 'cpf' ? '123.456.789-09' : '12.345.678/0001-99'})</option>
            <option value="both">Os dois (números + _formatted)</option>
          </select>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            Validação apenas pela quantidade de dígitos ({parseType === 'cpf' ? '11' : '14'}) — sem dígito verificador.
            {config.docOutputFormat === 'both' && (
              <> No modo "Os dois", a versão formatada fica em <code>{`{{${outputVariable}}}_formatted`}</code>.</>
            )}
          </p>
        </div>
      )}

      {/* Formato da data */}
      {parseType === 'date' && (
        <div>
          <label className={labelClass}>Formato de saída da data</label>
          <select
            className={selectClass}
            value={config.dateOutputFormat || 'iso'}
            onChange={(e) => onChange('dateOutputFormat', e.target.value)}
          >
            <option value="iso">ISO — aaaa-mm-dd (ex.: 2026-08-13)</option>
            <option value="br">BR — dd/mm/aaaa (ex.: 13/08/2026)</option>
            <option value="raw">Manter original (como o cliente digitou)</option>
          </select>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            Entrada aceita: <code>dd/mm/aaaa</code>, <code>dd-mm-aaaa</code>, <code>dd.mm.aaaa</code> e <code>aaaa-mm-dd</code>. Valida se é uma data real (mês 1–12, dia válido).
          </p>
        </div>
      )}
    </div>
  );
};
